import type { ErrorReporter } from '@estimating/error-reporting';
import { ApiRequestError } from '../shared/api/api-client';

type RootErrorHook = (error: unknown, errorInfo: { componentStack?: string | undefined }) => void;

/** The `createRoot` options React calls with render errors. */
export interface RootErrorHooks {
  readonly onCaughtError: RootErrorHook;
  readonly onUncaughtError: RootErrorHook;
  readonly onRecoverableError: RootErrorHook;
}

type Log = (message: string, error: unknown) => void;

/**
 * Reports every render error React sees, in one place: those an error boundary
 * caught (the app's, the router's and each section's, D-048), those nothing caught,
 * and those React recovered from. Each is also logged, as React's defaults do.
 *
 * A failed request that a section boundary caught (D-073) is logged but not reported:
 * the section already shows it with "Try again", and the API reports its own
 * unexpected errors (D-049).
 */
export function rootErrorHooks(
  errorReporter: ErrorReporter,
  log: Log = (message, error) => {
    console.error(message, error);
  },
): RootErrorHooks {
  const report =
    (message: string, handled: boolean): RootErrorHook =>
    (error, { componentStack }) => {
      log(message, error);
      errorReporter.captureException(error, {
        handled,
        ...(componentStack ? { componentStack } : {}),
      });
    };
  const reportCaught = report('Render error caught by an error boundary', true);

  return {
    onCaughtError: (error, errorInfo) => {
      if (error instanceof ApiRequestError) {
        log('Request failed; its section shows it', error);
        return;
      }
      reportCaught(error, errorInfo);
    },
    onUncaughtError: report('Uncaught render error', false),
    onRecoverableError: report('Render error React recovered from', true),
  };
}
